import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { LoginUserDto } from '../models/login-user-dto';
import { CheckerDataService } from './checker-data.service';

@Injectable({
  providedIn: 'root'
})
export class ConfigCheckerAuthService {
  private isLoggedInSubject = new BehaviorSubject<boolean>(false);
  isLoggedIn$ = this.isLoggedInSubject.asObservable();
  private usernameSubject = new BehaviorSubject<string | null>(null);
  username$ = this.usernameSubject.asObservable();
  private accessToken: string | null = null;

  constructor(
    private http: HttpClient,
    private checkerDataService: CheckerDataService
  ) {
    const electronAPI = (window as any).electronAPI;

    // Try to load token from localStorage on init
    this.accessToken = localStorage.getItem('config_checker_access_token');
    if (this.accessToken) {
      // Re-set cookie if in Electron even on restore
      if (electronAPI?.setCookie) {
        const homePage = this.checkerDataService.getHomePage();
        const baseUrl = homePage?.state?.persistentValues?.urlConfigChecker || 'https://api-de.biozoom.net';
        const url = new URL(baseUrl).origin;

        electronAPI.setCookie({
          url,
          name: 'jwt',
          value: this.accessToken,
          expirationDate: (Date.now() + 7 * 24 * 60 * 60 * 1000) / 1000
        }).catch((err: any) => console.error('Restore: Manual cookie set error:', err));
      }
      this.isLoggedInSubject.next(true);
    }
  }

  private getAuthUrl(): string {
    const homePage = this.checkerDataService.getHomePage();
    const baseUrl = homePage?.state?.persistentValues?.urlConfigChecker || 'https://api-de.biozoom.net';
    return `${baseUrl}/config-analyzer/auth`;
  }

  getAuthHeaders(): { [header: string]: string } {
    const headers: { [header: string]: string } = {
      Accept: 'application/json; charset=UTF-8'
    };
    if (this.accessToken) {
      headers.Authorization = `Bearer ${this.accessToken}`;
    }
    return headers;
  }

  login(credentials: LoginUserDto): Observable<any> {
    return this.http.post<any>(`${this.getAuthUrl()}/login`, credentials, { withCredentials: true }).pipe(
      tap((response) => {
        if (response && response.access_token) {
          this.accessToken = response.access_token;

          localStorage.setItem('config_checker_access_token', this.accessToken);

          // If running in Electron, set the cookie manually to bypass cross-domain restrictions
          const electronAPI = (window as any).electronAPI;
          if (electronAPI?.setCookie) {
            const homePage = this.checkerDataService.getHomePage();
            const baseUrl = homePage?.state?.persistentValues?.urlConfigChecker || 'https://api-de.biozoom.net';
            const url = new URL(baseUrl).origin;

            electronAPI.setCookie({
              url,
              name: 'jwt',
              value: this.accessToken,
              expirationDate: response.expires / 1000 // electron expects seconds
            }).catch((err: any) => console.error('Login: Manual cookie set error:', err));
          }

          this.isLoggedInSubject.next(true);
          this.usernameSubject.next(credentials.username);
        }
      })
    );
  }

  logout(): Observable<any> {
    return this.http.get(`${this.getAuthUrl()}/logout`, { responseType: 'text', withCredentials: true }).pipe(
      tap(() => {
        this.accessToken = null;
        localStorage.removeItem('config_checker_access_token');
        this.isLoggedInSubject.next(false);
        this.usernameSubject.next(null);
      })
    );
  }

  // get isLoggedIn(): boolean {
  //   return this.isLoggedInSubject.value;
  // }
}
